/**
 * CetusEd Community Portal — Login Page
 *
 * COMPLIANCE: FERPA §1232g(b) and IDEA §300.501(c) require authentication
 *   before granting access to student education records. This page offers
 *   two auth methods:
 *   1. Portal Code — student ID + 6-character access code issued by school
 *   2. Magic Link — email-based passwordless link (expires 15 min)
 *
 *   Login attempts SHOULD be rate-limited server-side to 5 attempts per
 *   minute per IP (NIST SP 800-63B §5.2.2.1).
 */

import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from './AuthProvider';
import { Card } from '../ui/components/Card';
import { Button } from '../ui/components/Button';

export const LoginPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<'portal-code' | 'magic-link'>('portal-code');
  const [studentId, setStudentId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  if (!auth) return null;
  if (auth.isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handlePortalCodeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.loginWithPortalCode(studentId, accessCode);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.loginWithMagicLink(email);
      setMagicLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <Card>
        <h1>CetusEd Community Portal</h1>
        <p className="login-description">
          Access student documents, sign consent forms, and communicate with school staff.
        </p>

        {magicLinkSent ? (
          <div className="login-magic-sent">
            <p>Check your email for a magic link. It expires in 15 minutes.</p>
            <Button onClick={() => setMagicLinkSent(false)}>Send again</Button>
          </div>
        ) : (
          <>
            {mode === 'portal-code' ? (
              <form onSubmit={handlePortalCodeLogin}>
                <div className="form-field">
                  <label htmlFor="studentId">Student ID</label>
                  <input
                    id="studentId"
                    type="text"
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="accessCode">Access Code</label>
                  <input
                    id="accessCode"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    required
                    autoComplete="off"
                    maxLength={10}
                  />
                </div>
                {error && <p className="login-error" role="alert">{error}</p>}
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>
                <p className="login-switch">
                  <button type="button" className="link-button" onClick={() => setMode('magic-link')}>
                    Use email magic link instead
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleMagicLinkRequest}>
                <div className="form-field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                  <p className="field-hint">
                    Use the email address on file with your student's school.
                  </p>
                </div>
                {error && <p className="login-error" role="alert">{error}</p>}
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Magic Link'}
                </Button>
                <p className="login-switch">
                  <button type="button" className="link-button" onClick={() => setMode('portal-code')}>
                    Use student ID and access code instead
                  </button>
                </p>
              </form>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
